/**
 * Site B — Lower Sable Dam (§2.2).
 *
 * Run-of-river on the mainstem: eight Kaplan units, fourteen tainter gates, 1.5 m of daily
 * pondage and essentially no flood storage. The game here is *passing* water in the right
 * proportions — 14-gate sequencing, TDG, the navigation lock pool, and the spring fish-passage
 * program — not storing it.
 *
 * The tailwater rating is a **table** rather than a power law because at high flow the tailwater
 * rises fast enough to collapse the net head, and a power law fitted at low flow would
 * misrepresent exactly the regime Scenario 9 is about.
 */

import type { SiteConfig } from '../engine/types';

const G = 9.81;
const C_FREE = 2.15;

export const lowerSable: SiteConfig = {
  id: 'lower-sable',
  name: 'Lower Sable Dam',
  river: 'Sable River',
  blurb:
    'Mainstem run-of-river: eight Kaplan units, fourteen gates, a navigation lock, and a metre and ' +
    'a half of pondage between you and a mistake.',

  dam: { crest: 122.0, parapet: 123.0, foundation: 88.0, fsl: 118.0, mfl: 119.5, mol: 116.5 },

  /**
   * Only 1.5 m of live range: ~0.045 × 10⁹ m³ across the operating band (§2.2), plus dead storage
   * below MOL so that touching 116.5 m is an operational hazard rather than an engine fault.
   * Live MOL → FSL is 0.100 − 0.055 = 0.045 × 10⁹ m³, as stated.
   */
  stageStorage: {
    h: [110.0, 113.0, 116.5, 117.0, 117.5, 118.0, 118.75, 119.5, 122.0],
    v: [0, 0.02e9, 0.055e9, 0.0698e9, 0.0848e9, 0.1e9, 0.1232e9, 0.1468e9, 0.229e9],
  },

  spillway: {
    kind: 'gated',
    sill: 104.0,
    gateCount: 14,
    gateWidth: 15.0,
    gateHeight: 14.0,
    dischargeCoeff: C_FREE,
    orificeCoeff: C_FREE / Math.sqrt(G),
    overtopCoeff: 1.7,
    hoistRate: 0.45,
    minNonVibrating: 0.3,
    adjacencyLimit: 1.5,
    // Centre-outward across fourteen bays.
    sequence: [6, 7, 5, 8, 4, 9, 3, 10, 2, 11, 1, 12, 0, 13],
    scourFlow: 1600,
    scourGateHours: 36,
    heaterPreheatMinutes: 90,
    iceThresholdC: -6,
    damagedGateFlowCap: 600,
  },

  llo: {
    count: 1,
    width: 2.5,
    height: 2.5,
    invert: 100.0,
    centreline: 101.25,
    cd: 0.8,
    travelRate: 0.5,
    tdgPercent: 120,
    cavitationMinFrac: 0.1,
    cavitationMaxFrac: 0.6,
    cavitationHwThreshold: 117.5,
    cavitationMinutes: 120,
  },

  powerhouse: {
    count: 8,
    ratedMW: 105,
    ratedHeadM: 19,
    ratedFlow: 606,
    maxFlow: 640,
    minLoadFrac: 0.25,
    roughZoneLo: 0.12,
    roughZoneHi: 0.25,
    roughZoneMinutes: 45,
    snlFlow: 45,
    intakeGateSeconds: 120,
    syncSeconds: 90,
    stopSeconds: 150,
    startCost: 900,
    tripHoldMinutes: 10,
    genEfficiency: 0.982,
    stationServiceMW: 5,
    penstockK: 4.0e-7,
    mva: 115,
    rpm: 85.7,
    governorMWPerS: 8,
    // Kaplan blades track load, so efficiency is a plateau, not a bowl: 0.93 across
    // q ∈ [0.5, 1.0], falling to 0.80 at q = 0.3 (§2.2).
    efficiency: {
      kind: 'flat-top',
      etaMax: 0.93,
      flatLo: 0.5,
      flatHi: 1.0,
      etaLowQ: 0.3,
      etaLowValue: 0.8,
      kh: 0.6,
      etaMin: 0.55,
    },
    wicket: { linear: 0.3, exp: 1.15 },
    rackDpDerateThreshold: 0.8,
    rakeMinutes: 40,
    tdgPercent: 102,
  },

  // Steep rating: net head collapses at high flow, which is the whole difficulty of Big Water.
  tailwater: {
    kind: 'table',
    q: [0, 1000, 2000, 3000, 4850, 7000, 9000, 12000, 16000],
    tw: [96.5, 97.6, 98.6, 99.5, 101.1, 103.2, 105.0, 107.4, 110.2],
  },

  downstream: {
    gaugeName: 'Sable Crossing',
    distanceKm: 28,
    muskingumK: 3.0,
    muskingumX: 0.25,
    localInflowBase: 120,
    localInflowName: 'Mill Creek',
    floodMinor: 11000,
    floodModerate: 13500,
    floodMajor: 16000,
  },

  licence: {
    minFlow: 900,
    minFlowWinter: 800,
    winterMonths: [12, 1, 2],
    downRampMPerH: 0.15,
    upRampMPerH: 0.4,
    tdgLimit: 110,
    // The fish-passage program waives the cap to 120 % (§2.2).
    tdgFloodLimit: 120,
    waiverDelayMinMinutes: 30,
    waiverDelayMaxMinutes: 90,
  },

  ruleCurve: {
    monthly: [117.6, 117.6, 117.5, 117.3, 117.2, 117.3, 117.5, 117.7, 117.8, 117.8, 117.7, 117.6],
    band: 0.6,
  },

  grid: {
    name: 'Sable Interconnection',
    exportLimitMW: 900,
    priceNight: 22,
    priceShoulder: 45,
    pricePeakMorning: 70,
    pricePeakEvening: 95,
    weekendFactor: 0.85,
    reserveObligationMW: 80,
    reservePayment: 6,
    reservePenalty: 60,
    agcPayment: 12,
    agcRangeMW: 60,
  },

  hydrology: {
    basinAreaKm2: 62000,
    meanInflow: 2100,
    bandWeights: [0.35, 0.4, 0.25],
    bandElevations: [700, 1400, 2100],
    refElevation: 120,
    sweArealMm: 220,
    ddf: 4.2,
    lapseRate: -0.0065,
    rainOnSnowFactor: 1.5,
    snowThresholdC: 1.0,
    uhRainPeakH: 24,
    uhMeltPeakH: 36,
    uhBaseH: 72,
    baseflowK: 0.99,
    baseflowRecharge: 0.2,
    runoffCMin: 0.3,
    runoffCMax: 0.7,
    wetnessTauDays: 12,
    evapMaxMmDay: 4.0,
    evapPeakDoy: 196,
    seepageBase: 1.0,
    upstreamLagH: 14,
    runoffScale: 1.0,
  },

  tdg: { base: 105, slope: 0.3, cap: 140, degasTauH: 14 },

  // Navigation lock: pool at or above 116.8 m from 06:00 to 22:00 (§2.2).
  lock: { minPool: 116.8, fromHour: 6, toHour: 22 },
};
